import { Link } from 'react-router-dom';
import type { IntegrationPublic } from '@marketing-hub/shared';
import { INTEGRATION_CONNECTOR_LABELS } from '@marketing-hub/shared';
import { ErrorState, LoadingState } from '@/components/StateMessage/StateMessage';
import { formatPercent } from '@/features/dashboard/kpiRollup';
import { sumGa4LastDays } from '@/features/dashboard/ga4Rollup';
import { sumGscLastDays } from '@/features/dashboard/gscRollup';
import { useGoogleDashboard } from '@/hooks/useGoogleDashboard';
import { SummaryMetricCard } from './SummaryMetricCard';
import styles from '../../pages/AnalyticsPage.module.css';

type Props = {
  rangeDays: 7 | 30 | 90;
  integrations: IntegrationPublic[];
};

export function AnalyticsGooglePanel({ rangeDays, integrations }: Props) {
  const googleQ = useGoogleDashboard(rangeDays);
  const ga4Int = integrations.find((i) => i.connector_type === 'ga4');
  const gscInt = integrations.find((i) => i.connector_type === 'gsc');

  const syncErrors = [ga4Int, gscInt].filter((i) => i?.status === 'error' && i.last_error);

  if (googleQ.isLoading) return <LoadingState />;
  if (googleQ.isError) {
    return <ErrorState message={(googleQ.error as Error).message} />;
  }

  const ga4Snapshots = googleQ.data?.ga4.snapshots ?? [];
  const gscSnapshots = googleQ.data?.gsc.snapshots ?? [];
  const ga4 = sumGa4LastDays(ga4Snapshots, rangeDays);
  const gsc = sumGscLastDays(gscSnapshots, rangeDays);
  const hasGoogleData = ga4.sessions > 0 || gsc.impressions > 0 || gsc.clicks > 0;

  return (
    <div className={styles.googleBlock}>
      {syncErrors.length > 0 ? (
        <div className={styles.syncAlert}>
          <strong>Google-sync mislukt</strong> — data komt niet binnen tot rechten kloppen.{' '}
          <Link to="/app/integraties">Integraties</Link>
          <ul className={styles.syncErrorList}>
            {syncErrors.map((i) =>
              i ? (
                <li key={i.id}>
                  {INTEGRATION_CONNECTOR_LABELS[i.connector_type]}:{' '}
                  {i.last_error?.slice(0, 120)}
                  {i.last_error && i.last_error.length > 120 ? '…' : ''}
                </li>
              ) : null,
            )}
          </ul>
        </div>
      ) : null}

      {!ga4Int && !gscInt ? (
        <p className={styles.muted}>
          Nog geen GA4/GSC-koppeling.{' '}
          <Link to="/app/integraties">Integraties instellen</Link> of bekijk{' '}
          <Link to="/app/dashboard">Dashboard</Link>.
        </p>
      ) : !hasGoogleData && syncErrors.length === 0 ? (
        <p className={styles.muted}>
          Koppeling actief, maar nog geen (of alleen nul-)snapshots in deze periode. GSC loopt ~3
          dagen achter op GA4 — gebruik Sync now (30 dagen backfill) bij{' '}
          <Link to="/app/integraties">Integraties</Link>.
        </p>
      ) : null}

      <div className={styles.summaryGrid}>
        <SummaryMetricCard label="GA4 — Sessions" value={String(ga4.sessions)} />
        <SummaryMetricCard label="GA4 — Active users" value={String(ga4.active_users)} />
        <SummaryMetricCard label="GA4 — Conversions" value={String(Math.round(ga4.conversions))} />
        <SummaryMetricCard
          label="GA4 — Engagement"
          value={formatPercent(ga4.engagement_rate)}
        />
        <SummaryMetricCard label="GSC — Clicks" value={String(gsc.clicks)} />
        <SummaryMetricCard label="GSC — Impressions" value={String(gsc.impressions)} />
        <SummaryMetricCard label="GSC — CTR" value={formatPercent(gsc.ctr)} />
        <SummaryMetricCard label="GSC — Positie (gem.)" value={gsc.position.toFixed(1)} />
      </div>
    </div>
  );
}
